import { useRef, useState } from "react";
import { useAppState } from "../state/store";
import { versionLabel } from "../version";

interface Props {
  onOpenGallery: () => void;
  onOpenExport: () => void;
  onOpenShare: () => void;
  onOpenAbout: () => void;
  onToggleSidePanel?: () => void;
  onToggleToolPanel?: () => void;
  onClearCanvas?: () => void;
  toolPanelOpen?: boolean;
  sidePanelOpen?: boolean;
}

export function TopBar({
  onOpenGallery,
  onOpenExport,
  onOpenShare,
  onOpenAbout,
  onToggleSidePanel,
  onToggleToolPanel,
  onClearCanvas,
  toolPanelOpen,
  sidePanelOpen,
}: Props) {
  const state = useAppState();
  const nameRef = useRef<HTMLInputElement>(null);
  const [editingName, setEditingName] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const saveBadge =
    state.doc.saveState === "dirty"
      ? "● unsaved"
      : state.doc.saveState === "saving"
        ? "⏳ saving…"
        : state.doc.saveState === "error"
          ? "⚠ save failed"
          : state.doc.savedAt
            ? `✓ saved ${new Date(state.doc.savedAt).toLocaleTimeString()}`
            : state.configError
              ? "local only (no cloud)"
              : "✓";

  const rename = async (value: string) => {
    setEditingName(false);
    const name = value.trim();
    if (name) await storeRename(name);
  };

  return (
    <header className="topbar">
      <div className="topbar-brand" onClick={onOpenGallery} title="Gallery">
        <span className="logo">🌿</span>
        <span className="brand-name">{state.config?.productName ?? "Frond"}</span>
        <span className="brand-env">{state.config?.environment ?? ""}</span>
      </div>

      <div className="doc-name">
        {editingName ? (
          <input
            ref={nameRef}
            autoFocus
            defaultValue={state.doc.name}
            onBlur={(e) => void rename(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void rename(e.currentTarget.value);
            }}
          />
        ) : (
          <button className="doc-name-text" onClick={() => setEditingName(true)} title="Rename">
            {state.doc.name || "Untitled canvas"}
          </button>
        )}
        <button
          className={`save-badge save-${state.doc.saveState} save-badge-btn`}
          onClick={() => void store.saveNow()}
          title="Click to save canvas (Ctrl+S)"
        >
          {saveBadge}
        </button>
      </div>

      <div className="topbar-actions">
        {/* Desktop actions */}
        <div className="topbar-actions-desktop">
          <button
            className={`btn subtle save-action-btn ${state.doc.saveState === "saving" ? "busy" : ""}`}
            onClick={() => void store.saveNow()}
            disabled={state.doc.saveState === "saving"}
            title="Save canvas to browser & cloud (Ctrl+S)"
          >
            {state.doc.saveState === "saving" ? "⏳ Saving…" : "💾 Save"}
          </button>
          {state.auth.status === "signed-in" ? (
            <button
              className="btn subtle account-btn"
              onClick={() => storeSignOut()}
              title="Signed in with Cognito. Click to sign out."
            >
              👤 {state.auth.email ?? "Account"}
            </button>
          ) : (
            <>
              <button className="btn subtle" onClick={() => void storeSignIn()} title="Sign in with AWS Cognito">
                Sign in
              </button>
              <button className="btn btn-accent" onClick={() => void storeSignUp()} title="Create an account with AWS Cognito">
                Sign up
              </button>
            </>
          )}
          {onToggleToolPanel && (
            <button
              className={`btn subtle ${toolPanelOpen ? "active" : ""}`}
              onClick={onToggleToolPanel}
              title="Toggle Tools palette"
            >
              🛠️ Tools
            </button>
          )}
          {onToggleSidePanel && (
            <button
              className={`btn subtle ${sidePanelOpen ? "active" : ""}`}
              onClick={onToggleSidePanel}
              title="Toggle Panels (Layers, Bookmarks, Palettes)"
            >
              🥞 Panels
            </button>
          )}
          <button className="btn subtle" onClick={onOpenShare}>
            Share
          </button>
          <button className="btn subtle" onClick={onOpenExport}>
            Export
          </button>
          {onClearCanvas && (
            <button className="btn subtle" onClick={onClearCanvas} title="Clear canvas and start fresh">
              ＋ New
            </button>
          )}
          <button className="btn subtle" onClick={onOpenGallery}>
            Gallery
          </button>
          <button
            className="btn subtle version-badge"
            onClick={onOpenAbout}
            title={`About Frond — ${versionLabel()}. Click for build details.`}
          >
            {versionLabel()}
          </button>
        </div>

        {/* Mobile actions */}
        <div className="topbar-actions-mobile">
          <button
            className="btn icon-btn mobile-menu-btn"
            onClick={() => setMenuOpen((o) => !o)}
            title="Menu"
            aria-label="Toggle menu"
          >
            ⋮
          </button>
          {menuOpen && (
            <>
              <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
              <div className="mobile-menu-dropdown">
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    void store.saveNow();
                    setMenuOpen(false);
                  }}
                >
                  <span>💾</span>
                  <span>Save canvas</span>
                </button>
                {onToggleToolPanel && (
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      onToggleToolPanel();
                      setMenuOpen(false);
                    }}
                  >
                    <span>🛠️</span>
                    <span>{toolPanelOpen ? "Hide Tools" : "Show Tools"}</span>
                  </button>
                )}
                {onToggleSidePanel && (
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      onToggleSidePanel();
                      setMenuOpen(false);
                    }}
                  >
                    <span>🥞</span>
                    <span>Layers & Bookmarks</span>
                  </button>
                )}
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    onOpenShare();
                    setMenuOpen(false);
                  }}
                >
                  <span>🔗</span>
                  <span>Share</span>
                </button>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    onOpenExport();
                    setMenuOpen(false);
                  }}
                >
                  <span>💾</span>
                  <span>Export</span>
                </button>
                {onClearCanvas && (
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      onClearCanvas();
                      setMenuOpen(false);
                    }}
                  >
                    <span>📄</span>
                    <span>New canvas</span>
                  </button>
                )}
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    onOpenGallery();
                    setMenuOpen(false);
                  }}
                >
                  <span>📁</span>
                  <span>Gallery</span>
                </button>
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    onOpenAbout();
                    setMenuOpen(false);
                  }}
                >
                  <span>ℹ️</span>
                  <span>About ({versionLabel()})</span>
                </button>
                <div className="mobile-menu-divider" />
                {state.auth.status === "signed-in" ? (
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      storeSignOut();
                      setMenuOpen(false);
                    }}
                  >
                    <span>👤</span>
                    <span>Sign out ({state.auth.email ?? "Account"})</span>
                  </button>
                ) : (
                  <>
                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        void storeSignIn();
                        setMenuOpen(false);
                      }}
                    >
                      <span>🔑</span>
                      <span>Sign in</span>
                    </button>
                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        void storeSignUp();
                        setMenuOpen(false);
                      }}
                    >
                      <span>✨</span>
                      <span>Sign up</span>
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

import { store } from "../state/store";

function storeRename(name: string): Promise<void> {
  return store.rename(name);
}

function storeSignIn(): Promise<void> {
  return store.signIn();
}

function storeSignUp(): Promise<void> {
  return store.signUp();
}

function storeSignOut(): void {
  store.signOut();
}
